import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  INITIAL_CHART_DATA,
  INITIAL_SELECTED_INDEXES,
  INITIAL_TABLE_DATA,
  INITIAL_VALUES_DATA
} from './constants';
import {
  areArraysEqual,
  getSampleSelectedIndexes,
  isSelectableLambdaRegressionRow
} from './utils';
import { LambdaWorkerInput } from './lambda.worker';
import { message } from 'antd';
import { OriginalChartPoint, SelectedIndexes, TableData } from '../../types';
import { useAppContext } from '../../../../../../providers/AppContext';
import { useSelected } from '../../../../../../providers/selectedContext';
import {
  CalculateSamplePayload,
  LambdaMethod,
  Sample
} from '../../../../../../@types/samples/types';
import { calculateSample } from '../../../../../../api/data';
import {
  buildLambdaSampleCurve,
  buildLiaoSampleCurve,
  clampCurvePointCount,
  DEFAULT_SAMPLE_CURVE_POINTS
} from '../../../../../utils/curveGeneration';

export function useLambdaAnalysis() {
  const { data, updateData } = useAppContext();
  const { selectedInfo, setSelectedInfo } = useSelected();

  const workerRef = useRef<Worker>();
  const [workerReady, setWorkerReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLocalCalculated, setIsLocalCalculated] = useState(false);
  const [tableData, setTableData] = useState(INITIAL_TABLE_DATA);
  const [valuesData, setValuesData] = useState(INITIAL_VALUES_DATA);
  const [selectedIndexes, setSelectedIndexes] = useState(
    INITIAL_SELECTED_INDEXES
  );
  const [chartData, setChartData] = useState(INITIAL_CHART_DATA);
  const [numberOfPoints, setNumberOfPoints] = useState(DEFAULT_SAMPLE_CURVE_POINTS);
  const [method, setMethod] = useState<LambdaMethod>('mean_slope');

  const originalSelectedSample = useMemo(
    () =>
      data.wellSamples.find(sample => sample.uid === selectedInfo.sample?.uid),
    [selectedInfo.sample?.uid, data.wellSamples]
  );

  const calculateInWorker = useCallback(
    (input: LambdaWorkerInput): Promise<Sample> => {
      const worker = workerRef.current;
      if (!worker) {
        return Promise.reject(new Error('Worker not initialized'));
      }

      return new Promise((resolve, reject) => {
        const handleMessage = (e: MessageEvent<Sample>) => {
          resolve(e.data);
          cleanup();
        };

        const handleError = (e: ErrorEvent) => {
          reject(new Error(e.message));
          cleanup();
        };

        const handleMessageError = (e: MessageEvent) => {
          reject(new Error('Failed to deserialize message from worker'));
          cleanup();
        };

        const cleanup = () => {
          worker.removeEventListener('message', handleMessage);
          worker.removeEventListener('error', handleError);
          worker.removeEventListener('messageerror', handleMessageError);
        };

        worker.addEventListener('message', handleMessage);
        worker.addEventListener('error', handleError);
        worker.addEventListener('messageerror', handleMessageError);

        worker.postMessage(input);
      });
    },
    []
  );

  const resetLocalCalculated = useCallback(() => {
    if (!originalSelectedSample) return;

    const originalSelectedIndexes = getSampleSelectedIndexes(
      originalSelectedSample,
      true
    );

    setSelectedInfo({ sample: originalSelectedSample });
    setIsLocalCalculated(false);
    setSelectedIndexes(originalSelectedIndexes);
  }, [originalSelectedSample, setSelectedInfo]);

  const handleCalculateRefinement = useCallback(async () => {
    if (!selectedInfo.sample) return;

    const mappedIndexes = Object.entries(selectedIndexes).map(
      ([index, selected]) => (selected ? Number(index) : null)
    );
    const filteredIndexes = mappedIndexes.filter(
      (index): index is number => index !== null
    );
    const payload: CalculateSamplePayload = {
      sample_uid: selectedInfo.sample.uid,
      strategies_keys: ['lambda'],
      input_data_map: {
        lambda: { type: 'LAMBDA', indexes: filteredIndexes, method }
      }
    };

    setLoading(true);
    try {
      const response = await calculateSample(payload);

      const newWellSamplesData = data.wellSamples.map(sample => {
        if (sample.uid === response.uid) {
          return response;
        }

        return sample;
      });
      updateData({ wellSamples: newWellSamplesData });
      setSelectedInfo({ sample: response });
    } catch (error: any) {
      console.error(error);
      const apiMessage = error?.response?.data?.message || error?.message;
      message.error(apiMessage || 'Something went wrong calculating Lambda. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [
    data.wellSamples,
    selectedIndexes,
    selectedInfo.sample,
    setSelectedInfo,
    updateData,
    method
  ]);

  const calculateLambda = useCallback(
    async (newSelectedIndexes: SelectedIndexes) => {
      if (!selectedInfo.sample) return;

      // Update the selectedIndexes with the new values
      const currentSelectedIndexes = {
        ...selectedIndexes,
        ...newSelectedIndexes
      };
      setSelectedIndexes(currentSelectedIndexes);

      if (!originalSelectedSample) return;

      const originalSampleSelectedIndexes = getSampleSelectedIndexes(
        originalSelectedSample
      );
      const sampleSelectedIndexes = getSampleSelectedIndexes(
        selectedInfo.sample
      );

      const filteredSampleSelectedIndexes = sampleSelectedIndexes.filter(
        (index): index is number => index !== null
      );

      const mappedCurrentlySelectedIndexes = Object.entries(
        currentSelectedIndexes
      ).map(([index, selected]) => (selected ? Number(index) : null));
      const filteredCurrentlySelectedIndexes =
        mappedCurrentlySelectedIndexes.filter(
          (index): index is number => index !== null
        );

      // If the currently selectedIndexes is different than the sample selected indexes,
      // the calculation should be done
      if (
        !areArraysEqual(
          filteredSampleSelectedIndexes,
          filteredCurrentlySelectedIndexes
        )
      ) {
        try {
          const result = await calculateInWorker({
            sample: selectedInfo.sample,
            indexes: filteredCurrentlySelectedIndexes,
            method
          });
          setSelectedInfo({ sample: result });
          setIsLocalCalculated(true);
        } catch (error) {
          if (error instanceof Error) {
            message.error(error.message);
            return;
          }
          message.error('Error trying to calculate lambda');
          return;
        }
      }

      // If the currently selectedInfo.sample have the exactly same selected indexes as the saved sample,
      // then set the localCalculated to false (considering that the calculation done by the worker
      // results the same as the api)
      if (
        areArraysEqual(originalSampleSelectedIndexes, sampleSelectedIndexes)
      ) {
        return setIsLocalCalculated(false);
      }
    },
    [
      selectedInfo.sample,
      selectedIndexes,
      setSelectedInfo,
      calculateInWorker,
      originalSelectedSample,
      method
    ]
  );

  // Recompute locally with a different solver method, keeping the current
  // point selection. The result is a local preview until "Save modifications"
  // persists it (the backend payload carries the same `method`).
  const changeMethod = useCallback(
    async (newMethod: LambdaMethod) => {
      setMethod(newMethod);
      if (!selectedInfo.sample) return;

      const mappedIndexes = Object.entries(selectedIndexes)
        .map(([index, selected]) => (selected ? Number(index) : null))
        .filter((index): index is number => index !== null);

      try {
        const result = await calculateInWorker({
          sample: selectedInfo.sample,
          indexes: mappedIndexes.length ? mappedIndexes : undefined,
          method: newMethod
        });
        setSelectedInfo({ sample: result });
        setIsLocalCalculated(true);
      } catch (error) {
        if (error instanceof Error) {
          message.error(error.message);
          return;
        }
        message.error('Error trying to calculate lambda');
      }
    },
    [selectedInfo.sample, selectedIndexes, calculateInWorker, setSelectedInfo]
  );

  // useEffect to set the worker ref
  useEffect(() => {
    workerRef.current = new Worker(
      new URL('./lambda.worker.ts', import.meta.url),
      {
        type: 'module'
      }
    );
    setWorkerReady(true);

    return () => {
      workerRef.current?.terminate();
      setWorkerReady(false);
    };
  }, []);

  // Run the worker on initial sample load so analysis_curves.lambda_curves (Pres/CorrectedSw)
  // is populated for the scatter points — the backend does not persist these arrays.
  // Depends on workerReady so it only fires after the worker is initialised.
  // No loop risk: after setSelectedInfo the originalSelectedSample memo returns the same
  // cached object (uid unchanged, wellSamples unchanged) so this effect does not re-fire.
  useEffect(() => {
    if (!workerReady || !originalSelectedSample) return;
    if (originalSelectedSample.analysis_curves?.lambda_curves?.Pres?.length) return;
    if (!originalSelectedSample.analysis_values?.lambda_values) return;

    const storedMethod =
      originalSelectedSample.analysis_values.lambda_values.method ?? 'mean_slope';

    calculateInWorker({ sample: originalSelectedSample, method: storedMethod })
      .then(result => setSelectedInfo(prev => {
        if (!prev.sample) return {};
        return {
          sample: {
            ...prev.sample,
            analysis_curves: {
              ...prev.sample.analysis_curves,
              lambda_curves: result.analysis_curves.lambda_curves
            },
            analysis_values: {
              ...prev.sample.analysis_values,
              lambda_values: result.analysis_values.lambda_values
            }
          }
        };
      }))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalSelectedSample, workerReady]);

  // useEffect to recalculate all the data used in the Lambda Analysis tab
  useEffect(() => {
    if (!selectedInfo.sample) return;

    const sampleCurves = selectedInfo.sample.input_sample_curves;
    const lambdaCurves = selectedInfo.sample.analysis_curves.lambda_curves;
    const lambdaValues = selectedInfo.sample?.analysis_values.lambda_values;

    const pc = sampleCurves.pc_model?.pc || [];
    const sw = sampleCurves.pc_model?.sw || [];
    const correctedsw = lambdaCurves?.CorrectedSw || [];
    const correctedpc = lambdaCurves?.CorrectedPc || [];
    const pcres = lambdaCurves?.Pres ?? [];
    const lnSwn = lambdaCurves?.ln_swn || [];
    const lnPc = lambdaCurves?.ln_pc || [];
    const lnSwnSquared = lambdaCurves?.ln_swn_squared || [];
    const lnPcSquared = lambdaCurves?.ln_pc_squared || [];
    const lnSwnXlnPc =
      lambdaCurves?.ln_swn_x_ln_pc ||
      (lambdaCurves as typeof lambdaCurves & { lnswn_x_lnpc?: (number | null)[] | null })
        ?.lnswn_x_lnpc ||
      [];
    const lambdaCurve = lambdaCurves?.lambda_curve || [];
    const indexes = lambdaCurves?.indexes;

    const original: Array<OriginalChartPoint | null> = pc.map((value, index) => {
      const swValue = correctedsw[index] ?? sw[index];
      const pcValue = pcres[index] ?? correctedpc[index] ?? value;
      if (
        typeof swValue !== 'number' ||
        typeof pcValue !== 'number' ||
        !Number.isFinite(swValue) ||
        !Number.isFinite(pcValue)
      ) {
        return null;
      }

      return {
        x: swValue,
        y: pcValue,
        rawIndex: index,
        selectable: isSelectableLambdaRegressionRow(
          lambdaCurves?.SwD?.[index],
          lambdaCurves?.PcD?.[index]
        )
      };
    });

    const originalFiltered = original.filter(
      (value): value is OriginalChartPoint => value !== null
    );

    const newSelectedIndexes = getSampleSelectedIndexes(selectedInfo.sample, true);

    const newTableData: TableData[] = pc.map((field, index) => {
      const validLambdaIndex = newSelectedIndexes[index];

      return {
        sw: sw[index],
        pc: field,
        correctedsw: correctedsw[index],
        correctedpc: correctedpc[index],
        pcres: pcres[index],
        lnSwn: validLambdaIndex ? lnSwn[index] : undefined,
        lnPc: validLambdaIndex ? lnPc[index] : undefined,
        lnSwnSquared: validLambdaIndex ? lnSwnSquared[index] : undefined,
        lnPcSquared: validLambdaIndex ? lnPcSquared[index] : undefined,
        lnSwnXlnPc: validLambdaIndex ? lnSwnXlnPc[index] : undefined,
        lambdaCurve: validLambdaIndex ? lambdaCurve[index] : undefined,
        selectable: isSelectableLambdaRegressionRow(
          lambdaCurves?.SwD?.[index],
          lambdaCurves?.PcD?.[index]
        ),
        key: `lambda-item-${index}`
      };
    });

    const pcMax = pcres
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
      .reduce((max, value) => Math.max(max, value), 0);
    // Use the lab Swir stored on the analysis (equal to the input swi),
    // falling back to the raw input, so the generated curve matches the solver.
    const swirForCalc =
      lambdaValues?.swir ?? selectedInfo.sample.input_sample_values?.swi;
    const calculatedLambda = lambdaValues
      ? buildLambdaSampleCurve({ ...lambdaValues, pc_max: pcMax }, swirForCalc, numberOfPoints)
      : [];

    const liaoSampleResults = selectedInfo.sample.analysis_values.liao;
    const calculatedLiao = liaoSampleResults
      ? buildLiaoSampleCurve(liaoSampleResults, numberOfPoints)
      : [];

    setTableData(newTableData);
    setSelectedIndexes(newSelectedIndexes);
    setChartData({
      calculated: calculatedLambda,
      original: originalFiltered,
      liao: calculatedLiao
    });
    setValuesData({
      pe: lambdaValues?.pe || 0,
      lambdaValue: lambdaValues?.lambda_value || 0,
      method: lambdaValues?.method ?? method,
      fitRmse: lambdaValues?.fit_rmse ?? null
    });
  }, [selectedInfo.sample, numberOfPoints, method]);

  // Keep the method selector in sync with whatever the selected sample was
  // saved with, so switching samples reflects the persisted solver.
  useEffect(() => {
    const storedMethod =
      originalSelectedSample?.analysis_values?.lambda_values?.method;
    if (storedMethod) setMethod(storedMethod);
  }, [originalSelectedSample?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    chartData,
    valuesData,
    selectedIndexes,
    tableData,
    isLocalCalculated,
    loading,
    numberOfPoints,
    setNumberOfPoints: (value: number) =>
      setNumberOfPoints(clampCurvePointCount(value, DEFAULT_SAMPLE_CURVE_POINTS)),
    resetLocalCalculated,
    handleCalculateRefinement,
    calculateLambda,
    method,
    changeMethod
  };
}
